import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const token = params.get("token");
  const [status, setStatus] = useState("checking");
  const [details, setDetails] = useState(null);
  const attempts = useRef(0);
  const maxAttempts = 10;

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setDetails(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("failed");
          return;
        }
        if (attempts.current < maxAttempts) {
          attempts.current += 1;
          setTimeout(poll, 2000);
        } else {
          setStatus("timeout");
        }
      } catch {
        if (!cancelled) setStatus("error");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="bg-white border border-[#DCD9CE] p-12 max-w-lg w-full text-center">
        {status === "checking" && (
          <>
            <p className="overline text-[#98604A] mb-3">Processing</p>
            <h1 className="font-serif-heading text-3xl mb-4">Confirming your payment…</h1>
            <p className="text-[#5C5C5C] text-sm">This usually takes just a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <p className="overline text-[#3F5746] mb-3">Payment Received</p>
            <h1 className="font-serif-heading text-4xl mb-4">Thank you.</h1>
            <p className="text-[#5C5C5C] mb-2">
              We've received ${(details?.amount_total / 100).toFixed(2)}.
            </p>
            <p className="text-[#5C5C5C] text-sm mb-8">
              You'll receive an email receipt shortly. We'll be in touch to schedule your restoration.
            </p>
            <Link to="/" className="btn-primary" data-testid="success-home-link">
              Back to home
            </Link>
          </>
        )}
        {status === "failed" && (
          <>
            <p className="overline text-[#B04E4E] mb-3">Payment Issue</p>
            <h1 className="font-serif-heading text-3xl mb-4">Something went wrong.</h1>
            <p className="text-[#5C5C5C] text-sm mb-6">Your card was not charged.</p>
            {token && (
              <Link to={`/quote/${token}`} className="btn-secondary">Try again</Link>
            )}
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <p className="overline text-[#98604A] mb-3">Pending</p>
            <h1 className="font-serif-heading text-3xl mb-4">Still processing.</h1>
            <p className="text-[#5C5C5C] text-sm mb-6">
              We're still confirming your payment. Check your email shortly — or contact us if you have questions.
            </p>
            <Link to="/" className="btn-secondary">Back to home</Link>
          </>
        )}
      </div>
    </div>
  );
}
